import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { CallButton } from "./CallButton";
import { useLang } from "../../i18n/LangContext";
import { useSettings } from "../../context/SettingsContext";

export const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <div role="group" aria-label="Language / Idioma" className="flex rounded-full border border-[#3F3F4E] p-0.5 text-xs font-bold">
      {[["en", "English"], ["es", "Español"]].map(([code, label]) => (
        <button
          key={code}
          type="button"
          data-testid={`lang-switch-${code}`}
          aria-pressed={lang === code}
          onClick={() => setLang(code)}
          className={`rounded-full px-3 py-1.5 transition-colors ${lang === code ? "bg-[#FACC15] text-black" : "text-gray-300 hover:text-white"}`}
        >
          <span className="sm:hidden">{code.toUpperCase()}</span>
          <span className="hidden sm:inline">{label}</span>
        </button>
      ))}
    </div>
  );
};

const NAV = [["/#services", "nav.services"], ["/#how", "nav.how"], ["/#area", "nav.area"]];

export const Header = () => {
  const { t, setLang } = useLang();
  const { settings, loaded } = useSettings();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (loaded && !localStorage.getItem("tow-lang") && settings.default_language) setLang(settings.default_language);
  }, [loaded, settings.default_language, setLang]);

  return (
    <header className="sticky top-0 z-40 border-b border-[#2E2E3A] bg-[#0A0A0C]/90 backdrop-blur-xl">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 focus:z-50 focus:rounded focus:bg-[#FACC15] focus:px-3 focus:py-2 focus:text-black">{t("nav.skip")}</a>
      <div className="container-x flex h-16 sm:h-20 items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" aria-label={settings.business_name}><Logo /></Link>
        <nav aria-label="Main" className="hidden lg:flex items-center gap-8 text-sm font-semibold text-gray-300">
          {NAV.map(([href, key]) => (
            <a key={href} href={href} className="hover:text-white transition-colors" data-testid={`nav-${key.split(".")[1]}`}>{t(key)}</a>
          ))}
          <NavLink to="/request" className="hover:text-white transition-colors" data-testid="nav-request">{t("nav.request")}</NavLink>
        </nav>
        <div className="flex items-center gap-3">
          <LangSwitch />
          <CallButton testId="nav-call-now-btn" className="hidden md:inline-flex px-5 py-2.5 text-sm" />
          <button type="button" className="lg:hidden rounded-lg p-2 text-white hover:bg-white/10" aria-expanded={open} aria-label={t("nav.menu")} data-testid="nav-menu-toggle" onClick={() => setOpen(!open)}>
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      {open && (
        <nav aria-label="Mobile" className="lg:hidden border-t border-[#2E2E3A] bg-[#0A0A0C]" data-testid="mobile-nav">
          <div className="container-x flex flex-col py-3">
            {NAV.map(([href, key]) => (
              <a key={href} href={href} onClick={() => setOpen(false)} className="py-3 text-base font-semibold text-gray-200 border-b border-white/5">{t(key)}</a>
            ))}
            <Link to="/request" onClick={() => setOpen(false)} className="py-3 text-base font-semibold text-[#FACC15]">{t("nav.request")}</Link>
          </div>
        </nav>
      )}
    </header>
  );
};
